/**
 * Interfejs opisujący pojedynczą zmienną szablonu świadectwa zgodności.
 */
export interface CertificateOfConformityVariable {
  /** Klucz szablonu w postaci {{nazwaKlucza}} */
  key: string;
  /** Etykieta wyświetlana na przycisku i w formularzu dokumentu */
  label: string;
  /**
   * Podpowiedź (tooltip) opisująca zastosowanie zmiennej.
   * Może być:
   * - string - statyczny tekst tooltipa
   * - Record<string, string> - mapowanie tooltipa w zależności od
   *   kombinacji producent + zabudowa (np. {"Mercedes Benz Tourist Line": "opis dla MB"})
   */
  tooltip: string | Record<string, string | number>;

  /**
   * Tekst tooltipa (fallback) używany gdy tooltip jest obiektem Record
   * i nie znaleziono dopasowania dla kombinacji producent+zabudowa.
   */
  tooltipFallback?: string;
  /** Grupa logiczna pola używana do sekcji formularza - sekcje numeryczne z dokumentu */
  group:
    | "0_general"
    | "0.1"
    | "0.2"
    | "0.2.1"
    | "0.2.2"
    | "0.2.2.1"
    | "0.2.3"
    | "0.4"
    | "0.5"
    | "0.5.1"
    | "0.6"
    | "0.9"
    | "0.10"
    | "0.11"
    | "footer"
    // Sekcja 1 - General construction characteristics
    | "1_general"
    | "1.1"
    | "2_steered"
    | "3_powered"
    // Main dimensions
    | "4_dimensions"
    | "5_width"
    | "7_height"
    | "9_distance"
    | "12_overhang"
    // Masses
    | "13_mass"
    | "16_max_masses"
    | "17_intended"
    | "18_towable"
    | "19_static"
    // Power plant
    | "20_engine"
    | "27_power"
    | "29_speed"
    // Axles and suspension
    | "30_axles"
    // Brakes
    | "36_brakes"
    // Bodywork
    | "38_bodywork"
    // Coupling
    | "44_coupling"
    // Environmental
    | "46_environmental"
    // Miscellaneous
    | "51_misc";

  /**
   * Gdy true, pole wymaga osobnej wartości tłumaczenia dla drugiego języka
   * w trybie dokumentu dwujęzycznego.
   */
  requiredTranslation?: boolean;

  /**
   * Wartość domyślna dla pola. Może być:
   * - string | number - statyczna wartość
   * - string[] - statyczna wartość dla pól multiselect
   * - Record<string, string | number | string[]> - mapowanie wartości w zależności od
   *   kombinacji producent + zabudowa (np. {"Mercedes Benz Tourist Line": "TEST1"}
   *   lub dla multiselect {"Iveco Tourist Line": ["TPMS", "ESS"]})
   */
  value?:
    | string
    | number
    | string[]
    | Record<string, string | number | string[]>;

  /**
   * Wartość domyślna (fallback) używana gdy value jest obiektem Record
   * i nie znaleziono dopasowania dla kombinacji producent+zabudowa.
   */
  valueFallback?: string | number | string[];

  /**
   * Informuje, że pole powinno być językowo-zależne (oddzielna wartość na język).
   * Gdy true, przy kopiowaniu danych między językami wartość nie jest automatycznie przepisywana.
   */
  flexible?: boolean;
  /**
   * Gdy true, pole jest zablokowane do edycji w formularzu.
   */
  disabled?: boolean;
  /**
   * Gdy true, pole jest ukryte w formularzu, ale nadal może być używane w szablonie.
   */
  hidden?: boolean;
  /**
   * Gdy true, pole zawsze pochodzi z danych zamówienia (Order)
   * i nie powinno być zapisywane w fieldValues rekordu dokumentu.
   */
  orderDerived?: boolean;
  /**
   * Ścieżka dot-notation do pola w obiekcie zamówienia.
   */
  orderPath?: string;
  /**
   * Sposób formatowania wartości pobranej z zamówienia:
   * - 'date'     → format DD.MM.YYYY
   * - 'currency' → kwota z walutą
   * - 'computed' → wartość obliczana przez dedykowaną funkcję
   * - 'text'     → bez transformacji (wartość zwracana as-is)
   */
  formatter?: "date" | "currency" | "text" | "computed";
  /**
   * Gdy true, przy renderowaniu szablonu wartość pola jest formatowana jako kwota
   * z walutą z kontrolki {{currency}}.
   */
  renderAsCurrency?: boolean;

  /**
   * Gdy true, przy renderowaniu szablonu do niepustej wartości (innej niż
   * "N/A"/"NA"/"-" i podobne, patrz appendKgIfMissing w komponencie) dopisywane
   * jest " kg", jeśli użytkownik sam tego nie zrobił - zabezpieczenie przed
   * zapomnieniem jednostki w polach mas wpisywanych ręcznie.
   */
  renderAppendKg?: boolean;

  /**
   * Zmienna, której wartość jest inna w zależności od homologacji
   * CERTIFICADO DE HOMOLOGACIÓN
   */
  homologation?: boolean;
  /** Typ pola formularza w CertificateOfConformityDocumentComponent */
  inputType?:
    | "text"
    | "number"
    | "date"
    | "textarea"
    | "select"
    | "multiselect";
  /** Lista opcji dla pól typu select */
  selectOptions?: Array<{ value: string; label: string }>;
  /** Szerokość pola w siatce: 1 = standardowa (domyślnie), 2 = podwójna */
  colSpan?: 1 | 2;
}

/**
 * Lista zmiennych szablonu świadectwa zgodności.
 * Zmienne pogrupowane według sekcji numerycznych z dokumentu homologacyjnego.
 */
export const CERTIFICATE_OF_CONFORMITY_VARIABLES: CertificateOfConformityVariable[] =
  [
    // 0.1 Make (Trade name of manufacturer)
    {
      key: "{{make}}",
      label: "Marka (nazwa handlowa producenta)",
      tooltip: "0.1 - Marka pojazdu (trade name of manufacturer)",
      group: "0.1",
      inputType: "text",
      colSpan: 2,
      value: {
        "Mercedes Benz Tourist Line": "CUBY",
        "Mercedes Benz VIP Line": "CUBY",
        "Mercedes Benz Special Line": "CUBY",
        "Mercedes Benz Tourist Line HighDeck ": "CUBY",
        "Mercedes Benz City Line": "CUBY",
        "Iveco Tourist Line": "CUBY IVECO",
        "Iveco City Line": "CUBY IVECO",
      },
      hidden: true,
    },

    // 0.2 Type, Variant, Version
    {
      key: "{{type}}",
      label: "Typ",
      group: "0.2",
      tooltip: {
        "Mercedes Benz Tourist Line":
          "Typ pojazdu, określany przez Autocuby, np. 906 KA 50/530",
        "Mercedes Benz Tourist Line HighDeck ":
          "Typ pojazdu, określany przez Autocuby, np. 906CA55",
        "Iveco Tourist Line":
          "Typ pojazdu, określany przez Autocuby, np. IS72CI2DA",
      },
      tooltipFallback:
        "0.2 - Typ pojazdu, określany przez Autocuby, np. IS72CI2DA dla Iveco",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "906 KA 50/530",
        "Mercedes Benz Tourist Line HighDeck ": "906CA55",
        "Iveco Tourist Line": "IS72CI2DA",
      },
    },
    // {
    //   key: "{{variant}}",
    //   label: "Wariant",
    //   tooltip: "0.2 - Wariant pojazdu (Wariant pojazdu bazowego)",
    //   group: "0.2",
    //   inputType: "text",
    //   value: {
    //     "Mercedes Benz Tourist Line": "V2W61500DXX",
    //     "Mercedes Benz Tourist Line HighDeck ": "FFW61500DXX",
    //     "Iveco Tourist Line": "IU11C1C",
    //   },
    // },
    {
      key: "{{vehicleClassSimplified}}",
      label: "Klasa pojazdu - wariant",
      tooltip:
        "Klasa pojazdu: class I/Class II/Class III/Class A/Class B. Powyżej 22 pasażerów: 1 - miejski (city) miejsca stojące i siedzące / 2 - międzymiastowy - miejsca siedzące i stojące / 3 - tylko siedzące / Do 22 pasażerów:  Class A - miejski do 22 pasażerów (siedzących i stojących razem) /  Class B - turystyczny",
      group: "0.2",
      inputType: "select",
      selectOptions: [
        { value: "1", label: "1" },
        { value: "2", label: "2" },
        { value: "3", label: "3" },
        { value: "A", label: "A" },
        { value: "B", label: "B" },
      ],
      formatter: "computed",
      disabled: true,
    },
    // Podpola dla wersji (widoczne tylko dla określonych kombinacji)
    // Iveco Tourist Line: Tonaż + Rozstaw osi + Bagażnik + Bagażniki boczne
    // MB Tourist Line HighDeck: Tonaż + Rozstaw osi + P/N + R0/R1/R3 + L0/L1/L2 + M0/M1
    {
      key: "{{versionTonnage}}",
      label: "Tonaż",
      group: "0.2",
      inputType: "text",
      tooltip: "Tonaż pojazdu (np. 68, 72)",
      hidden: true,
      disabled: true,
      formatter: "computed",
      // selectOptions: [
      //   { value: "68W", label: "68" },
      //   { value: "70W", label: "70" },
      //   { value: "72W", label: "72" },
      // ],
    },
    {
      // UWAGA: opcje wyboru dla tego pola NIE pochodzą z selectOptions (jak
      // dla pozostałych podpól wersji) - {{versionWheelbase}} jest wyjątkiem,
      // renderowanym w komponencie na podstawie dynamicznego
      // wheelbaseOptions (patrz getWheelbaseSelectOptions w
      // wheelbase-utils.ts - lista faktycznie zadeklarowanych wariantów per
      // zabudowa, różna dla Iveco i Mercedes HighDeck).
      key: "{{versionWheelbase}}",
      label: "x osi",
      group: "0.2",
      inputType: "select",
      tooltip: "Rozstaw osi",
      hidden: true,
    },
    {
      key: "{{versionPNR}}",
      label: "P/N",
      group: "0.2",
      inputType: "select",
      tooltip: "P lub N. P - Pilotka, N - bez Pilotki",
      selectOptions: [
        { value: "P", label: "P" },
        { value: "N", label: "N" },
      ],
      hidden: true,
      formatter: "computed",
      disabled: true,
    },
    {
      key: "{{versionTrunk}}",
      label: "Bagażnik",
      group: "0.2",
      inputType: "select",
      tooltip: {
        "Mercedes Benz Tourist Line": "",
        "Mercedes Benz Tourist Line HighDeck ":
          "R1 - bagażnik najmniejszy, R2 - średni, R3 - największy",
        "Iveco Tourist Line":
          "Bagażnik z tyłu - R1 mały / R2 średni bagażnik / R3 duży",
      },
      // tooltip: "R0 - brak / R1 - tylny / R3 - najmniejszy",
      selectOptions: [
        { value: "R0", label: "R0" },
        { value: "R1", label: "R1" },
        { value: "R2", label: "R2" },
        { value: "R3", label: "R3" },
      ],
      value: {
        "Iveco Tourist Line": "R0",
      },
      hidden: true,
    },
    {
      key: "{{versionSideTrunks}}",
      label: "Bagażniki boczne",
      group: "0.2",
      inputType: "select",

      tooltip: {
        "Mercedes Benz Tourist Line": "",
        "Mercedes Benz Tourist Line HighDeck ":
          "L0 - bez bocznej przestrzeni bagażowej / L1 - Boczna przestrzeń bagażowa tylko po jednej stronie pojazdu (lewej lub prawej) / L2 - Boczna przestrzeń bagażowa po obu stronach pojazdu",
        "Iveco Tourist Line": "L0 lub L1 - jest lub nie ma",
      },
      selectOptions: [
        { value: "L0", label: "L0" },
        { value: "L1", label: "L1" },
        { value: "L2", label: "L2" },
      ],
      value: {
        "Iveco Tourist Line": "L0",
      },
      hidden: true,
    },
    {
      key: "{{versionSpace}}",
      label: "Przestrzeń między kuframi",
      group: "0.2",
      inputType: "select",
      tooltip:
        "M0 - Brak środkowej przestrzeni bagażowej / M1 - 	Obecność środkowej przestrzeni bagażowej",
      selectOptions: [
        { value: "M0", label: "M0" },
        { value: "M1", label: "M1" },
      ],
      hidden: true,
    },
    // Podpola wersji tylko dla zwykłego "Mercedes Benz Tourist Line" (NIE
    // HighDeck) - patrz utils/mercedes-tourist-line-version.ts. Wersja tej
    // zabudowy to: versionPassengersCode + versionMassCode + versionWheelchairCode.
    {
      key: "{{versionPassengersCode}}",
      label: "Kod miejsc",
      group: "0.2",
      inputType: "select",
      tooltip:
        "Kod ilości miejsc. Wyliczany z liczby pasażerów i opcji przedłużenia tylnego (przedłużenie: 22 pas. → 19, 19 pas. → 18; bez: 19 pas. → 08, 16 pas. → 10; wózki → 37).",
      // Zakres wg fabrycznej tabeli wariantów (tourist-line-table.html) -
      // wszystkie kody osiągalne dla klas masy C/F, patrz
      // utils/mass-data/mercedes-tourist-line-mass-data.ts.
      selectOptions: [
        { value: "01", label: "01" },
        { value: "02", label: "02" },
        { value: "03", label: "03" },
        { value: "04", label: "04" },
        { value: "05", label: "05" },
        { value: "06", label: "06" },
        { value: "07", label: "07" },
        { value: "08", label: "08" },
        { value: "09", label: "09" },
        { value: "10", label: "10" },
        { value: "11", label: "11" },
        { value: "12", label: "12" },
        { value: "15", label: "15" },
        { value: "16", label: "16" },
        { value: "17", label: "17" },
        { value: "18", label: "18" },
        { value: "19", label: "19" },
        { value: "20", label: "20" },
        { value: "21", label: "21" },
        { value: "23", label: "23" },
        { value: "26", label: "26" },
        { value: "27", label: "27" },
        { value: "28", label: "28" },
        { value: "29", label: "29" },
        { value: "30", label: "30" },
        { value: "32", label: "32" },
        { value: "33", label: "33" },
        { value: "34", label: "34" },
        { value: "36", label: "36" },
        { value: "37", label: "37" },
        { value: "38", label: "38" },
        { value: "39", label: "39" },
      ],
      hidden: true,
      formatter: "computed",
    },
    {
      key: "{{versionMassCode}}",
      label: "Masa",
      group: "0.2",
      inputType: "select",
      tooltip:
        "Kod masy: C – 5500 kg, F – 6100 kg. Wyliczany z masy pojazdu (≥ 6000 kg → F).",
      selectOptions: [
        { value: "C", label: "C" },
        { value: "F", label: "F" },
      ],
      hidden: true,
      formatter: "computed",
    },
    {
      key: "{{versionWheelchairCode}}",
      label: "Wózki inwalidzkie",
      group: "0.2",
      inputType: "select",
      tooltip:
        "Kod wózków inwalidzkich: P – bez wózków, W – z wózkami. Wyliczany z wariantu miejsc.",
      selectOptions: [
        { value: "P", label: "P" },
        { value: "W", label: "W" },
      ],
      hidden: true,
      formatter: "computed",
    },
    {
      key: "{{version}}",
      label: "Wersja - rozszerzenie wersji pojazdu bazowego",
      group: "0.2",
      inputType: "text",
      formatter: "computed",
      tooltip: {
        "Mercedes Benz Tourist Line": "",
        "Mercedes Benz Tourist Line HighDeck ":
          "Wersja 1 etapu + dodatkowe oznaczenie, np. 68W12PR1L1M0 - Tonaż np. 68 | Rozstaw osi | P lub  | R1 bagażniki tylne / R0 brak bagażnika tylnego / R3 - najmniejszy bagażnik | L2 bagażniki boczne | M0 przestrzeń między kuframi",
        "Iveco Tourist Line":
          "Tonaż | Rozstaw osi | bagażniki | bagażniki boczne",
      },
      disabled: true,
    },

    // 0.2.1 Commercial name
    {
      key: "{{commercialName}}",
      label: "Nazwa handlowa",
      tooltip: "0.2.1 - Nazwa handlowa pojazdu (np. Sprinter HD)",
      group: "0.2.1",
      inputType: "text",
      colSpan: 2,
      value: {
        "Mercedes Benz Tourist Line": "SPRINTER",
        "Mercedes Benz VIP Line": "N/A",
        "Mercedes Benz Special Line": "N/A",
        "Mercedes Benz Tourist Line HighDeck ": "Sprinter HD",
        "Mercedes Benz City Line": "N/A",
        "Iveco Tourist Line": "N/A",
        "Iveco City Line": "N/A",
      },
      hidden: true,
    },

    // 0.2.2 Type-approval information of the base vehicle
    {
      key: "{{baseVehicleType}}",
      label: "Typ pojazdu bazowego",
      tooltip: "0.2.2 - Typ pojazdu bazowego",
      group: "0.2.2",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "906BB50",
        "Mercedes Benz Tourist Line HighDeck ": "906BA50",
        "Iveco Tourist Line": "IS72CI2DA",
      },
      hidden: true,
    },
    {
      key: "{{baseVehicleVariant}}",
      label: "Wariant pojazdu bazowego",
      tooltip: "0.2.2 - Wariant pojazdu bazowego",
      group: "0.2.2",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "V2W61500DXX",
        "Mercedes Benz Tourist Line HighDeck ": "FFW61500DXX",
        "Iveco Tourist Line": "IU21C1C",
      },
    },
    {
      key: "{{baseVehicleVersion}}",
      label: "Wersja pojazdu bazowego",
      tooltip: "0.2.2 - Wersja pojazdu bazowego",
      group: "0.2.2",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "SG5EBKA2XXAX",
        "Mercedes Benz Tourist Line HighDeck ": "RG5EBNA2XXAX",
        "Iveco Tourist Line": "DNAMA1FX75",
      },
    },
    {
      key: "{{typeApprovalNumber}}",
      label: "Numer homologacji bazowej",
      tooltip:
        "0.2.2 - Numer homologacji bez numeru rozszerzenia (np. e1*2007/46*0294)",
      group: "0.2.2",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "e1*2007/46*0296",
        "Mercedes Benz Tourist Line HighDeck ": "e1*2007/46*0294",
        "Iveco Tourist Line": "e3*2007/46*0125",
      },
      disabled: true,
    },
    {
      key: "{{typeApprovalNumberExtension}}",
      label: "Rozszerzenie homologacji bazowej",
      tooltip: "0.2.2 - Numer rozszerzenia homologacji bazowej (np. 21)",
      group: "0.2.2",
      inputType: "number",
    },

    // 0.2.2.1 Allowed Parameter Values for multistage type approval
    {
      key: "{{finalVehicleActualMass}}",
      label: "Masa rzeczywista pojazdu końcowego",
      tooltip: "0.2.2.1 - Final Vehicle actual mass (kg)",
      group: "0.2.2.1",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{finalVehicleMaxLadenMass}}",
      label: "Dopuszczalna masa całkowita pojazdu końcowego",
      tooltip:
        "0.2.2.1 - Final Vehicle technically permissible maximum laden mass (kg)",
      group: "0.2.2.1",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{frontalArea}}",
      label: "Powierzchnia czołowa",
      tooltip: "0.2.2.1 - Frontal area for final vehicle (cm²)",
      group: "0.2.2.1",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{rollingResistance}}",
      label: "Opory toczenia",
      tooltip: "0.2.2.1 - Rolling resistance (kg/t)",
      group: "0.2.2.1",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{frontGrilleArea}}",
      label: "Powierzchnia przekroju wlotu powietrza",
      tooltip:
        "0.2.2.1 - Cross-sectional area of air entrance of the front grille (cm²)",
      group: "0.2.2.1",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },

    // 0.2.3 Identifiers
    {
      key: "{{interpolationFamilyId}}",
      label: "Identyfikator rodziny interpolacji",
      tooltip: "0.2.3.1 - Interpolation family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
    },
    {
      key: "{{atctFamilyId}}",
      label: "Identyfikator rodziny ATCT",
      tooltip: "0.2.3.2 - ATCT family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{pemsFamilyId}}",
      label: "Identyfikator rodziny PEMS",
      tooltip: "0.2.3.3 - PEMS family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
    },
    {
      key: "{{roadloadFamilyId}}",
      label: "Identyfikator rodziny obciążenia drogowego",
      tooltip: "0.2.3.4 - Roadload family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{roadloadMatrixFamilyId}}",
      label: "Identyfikator rodziny macierzy obciążenia drogowego",
      tooltip: "0.2.3.5 - Roadload Matrix family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
    },
    {
      key: "{{periodicRegenerationFamilyId}}",
      label: "Identyfikator rodziny regeneracji okresowej",
      tooltip: "0.2.3.6 - Periodic regeneration family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{evaporativeTestFamilyId}}",
      label: "Identyfikator rodziny testu parowania",
      tooltip: "0.2.3.7 - Evaporative test family's identifier",
      group: "0.2.3",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },

    // 0.4 Vehicle category
    {
      key: "{{vehicleCategory}}",
      label: "Kategoria pojazdu",
      tooltip: "0.4 - Kategoria pojazdu (np. M3)",
      group: "0.4",
      inputType: "text",
      formatter: "computed",
      orderDerived: true,
      orderPath: "order.vehicleWeight.weight",
      hidden: true,
      disabled: true,
    },

    // 0.5 Company name and address of manufacturer
    {
      key: "{{manufacturerCompanyName}}",
      label: "Nazwa i adres firmy producenta",
      tooltip: "0.5 - Nazwa i adres firmy producenta pojazdu",
      value: `Auto-CUBY Sp. z o.o. sp. komandytowa <br> Przemysłowa 2, 84-241 Gościcino, Poland`,
      group: "0.5",
      inputType: "text",
      colSpan: 2,
      hidden: true,
    },
    // 0.5.1 Name and address of the manufacturer of the base vehicle
    {
      key: "{{baseVehicleManufacturerNameAndAddress}}",
      label: "Nazwa i adres producenta pojazdu bazowego",
      tooltip: "0.5.1 - Nazwa producenta pojazdu bazowego",
      group: "0.5.1",
      inputType: "text",
      colSpan: 2,
      hidden: true,
      value: {
        "Mercedes Benz Tourist Line":
          "Mercedes Benz AG<br>DE-70372 STUTTGARD, Germany",
        "Mercedes Benz VIP Line":
          "Mercedes Benz AG<br>DE-70372 STUTTGARD, Germany",
        "Mercedes Benz Special Line":
          "Mercedes Benz AG<br>DE-70372 STUTTGARD, Germany",
        "Mercedes Benz Tourist Line HighDeck ":
          "Mercedes Benz AG<br>DE-70372 STUTTGARD, Germany",
        "Mercedes Benz City Line":
          "Mercedes Benz AG<br>DE-70372 STUTTGARD, Germany",
        "Iveco Tourist Line":
          "IVECO S.P.A- I- VIA PUGLIA, 35<br>10-156 TORINO, ITALY",
        "Iveco City Line":
          "IVECO S.P.A- I- VIA PUGLIA, 35<br>10-156 TORINO, ITALY",
      },
    },
    // 0.6 Location and method of attachment of the statutory plates
    {
      key: "{{statutoryPlatesLocationFirstStage}}",
      label: "Lokalizacja tablic homologacyjnych 1 etap",
      tooltip:
        "0.6 - Lokalizacja i metoda mocowania tablic homologacyjnych 1 etap",
      group: "0.6",
      inputType: "text",
      colSpan: 2,
      value: {
        "Mercedes Benz Tourist Line": "in the step well",
        "Mercedes Benz Tourist Line HighDeck ":
          "on driver's seat console, glued",
        "Iveco Tourist Line":
          "in the engine compartment, rear and on driver's seat console, glued",
      },
      hidden: true,
    },
    {
      key: "{{statutoryPlatesLocationSecondStage}}",
      label: "Lokalizacja tablic homologacyjnych 2 etap",
      tooltip:
        "0.6 - Lokalizacja i metoda mocowania tablic homologacyjnych 2 etap",
      group: "0.6",
      inputType: "text",
      colSpan: 2,
      value: {
        "Mercedes Benz Tourist Line":
          "on the cross member of the front radiator grill",
        "Mercedes Benz Tourist Line HighDeck ":
          "next to first stage statutory plate",
        "Iveco Tourist Line": "under guide seat",
      },
      hidden: true,
    },
    {
      key: "{{vinLocation}}",
      label: "Lokalizacja numeru VIN",
      tooltip: "0.6 - Lokalizacja numeru identyfikacyjnego pojazdu (VIN)",
      group: "0.6",
      inputType: "text",
      colSpan: 2,
      value: "statutory plate",
      hidden: true,
    },

    // 0.9 Name and address of the manufacturer's representative
    {
      key: "{{manufacturerRepresentative}}",
      label: "Przedstawiciel producenta",
      tooltip: "0.9 - Nazwa i adres przedstawiciela producenta (jeśli dotyczy)",
      group: "0.9",
      inputType: "textarea",
      colSpan: 2,
      requiredTranslation: true,
      value: "N/A",
      hidden: true,
    },

    // 0.10 Vehicle identification number
    {
      key: "{{vinNumber}}",
      label: "Numer VIN",
      tooltip: "0.10 - Numer identyfikacyjny pojazdu (VIN)",
      group: "0.10",
      orderPath: "vinNumber",
      orderDerived: true,
      formatter: "computed",
      inputType: "text",
      colSpan: 2,
      hidden: true,
    },

    // 0.11 Date of manufacture of the vehicle
    {
      key: "{{manufactureDate}}",
      label: "Data produkcji pojazdu = wystawienia COC",
      tooltip:
        "0.11 - Data produkcji pojazdu / Data wystawienia COC widoczna w stopce obok Miejscowości",
      group: "0.11",
      inputType: "date",
      formatter: "computed",
    },
    {
      key: "{{podpis}}",
      label: "Podpis",
      tooltip: "",
      group: "0.11",
      inputType: "text",
      hidden: true,
    },

    // 0.11 (a) - Vehicle completion description
    {
      key: "{{vehicleCompletionDescription}}",
      label: "Opis zakończenia pojazdu (a)",
      tooltip:
        "0.11(a) - Opis zakończenia pojazdu, np. 'change of vehicle category N2 (type BA) to M3 (type CA)'",
      group: "0.11",
      inputType: "text",
      colSpan: 2,
      requiredTranslation: true,
      value: {
        "Mercedes Benz Tourist Line": "conversion to single deck Bus Class B",
        "Mercedes Benz Tourist Line HighDeck ":
          "change of vehicle category N2 (type BA) to M3 (type CA)",
        "Iveco Tourist Line": "conversion to single deck Bus Class III",
      },
    },

    // 0.11 (b) - Type approval information
    {
      key: "{{completionTypeApprovalNumber}}",
      label: "Numer homologacji typu (b)",
      tooltip: "0.11(b) - Numer homologacji typu, np. 'e9*2007/46*6529*05'",
      group: "0.11",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "e9*2007/46*0310*16",
        "Mercedes Benz Tourist Line HighDeck ": "e9*2007/46*6529*05",
        "Iveco Tourist Line": "e9*2007/46*6425*06",
      },
      hidden: true,
      homologation: true,
    },
    {
      key: "{{typeApprovalDate}}",
      label: "Data wydania homologacji (b)",
      tooltip: "0.11(b) - Data wydania homologacji typu",
      group: "0.11",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "12.06.2026",
        "Mercedes Benz Tourist Line HighDeck ": "23.02.2024",
        "Iveco Tourist Line": "12.07.2023",
      },
      hidden: true,
      homologation: true,
    },

    // 0.11 (c) - Registration conditions
    {
      key: "{{trafficHand}}",
      label: "Ruch drogowy (lewy/prawy) (c)",
      tooltip: "0.11(c) - right/left - ruch lewo/prawostronny",
      group: "0.11",
      inputType: "select",
      selectOptions: [
        { value: "right", label: "right" },
        { value: "left", label: "left" },
      ],
      value: "right",
      hidden: true,
    },
    {
      key: "{{speedometerUnits}}",
      label: "Jednostki prędkościomierza (c)",
      tooltip: "0.11(c) - metric/imperial - jednostki metryczne/imperialne",
      group: "0.11",
      inputType: "select",
      selectOptions: [
        { value: "metric", label: "metric" },
        { value: "imperial", label: "imperial" },
      ],
      value: "metric",
      hidden: true,
    },

    // Footer - Place and date
    {
      key: "{{place}}",
      label: "Miejsce",
      tooltip: "Miejsce na dole dokumentu",
      group: "footer",
      inputType: "text",
      colSpan: 2,
      value: "Gościcino",
      hidden: true,
    },

    // === SEKCJA 1 - General construction characteristics ===
    {
      key: "{{numberOfAxles}}",
      label: "Liczba osi",
      tooltip: "1 - Number of axles (np. 2)",
      group: "1_general",
      inputType: "number",
      value: "2",
      hidden: true,
    },
    {
      key: "{{numberOfWheels}}",
      label: "Liczba kół",
      tooltip: "1 - and wheels (np. 6)",
      group: "1_general",
      inputType: "number",
      value: "6",
      hidden: true,
    },
    {
      key: "{{twinWheelsAxleNumber}}",
      label: "Oś z kołami bliźniaczymi (nr)",
      tooltip: "1.1 - Number and position of axles with twin wheels - axle",
      group: "1.1",
      inputType: "text",
      value: "1",
      hidden: true,
    },
    {
      key: "{{twinWheelsCount}}",
      label: "Ilość kół bliźniaczych",
      tooltip: "1.1 - count",
      group: "1.1",
      inputType: "text",
      value: "A2",
      hidden: true,
    },
    {
      key: "{{steeredAxlesNumber}}",
      label: "Liczba osi skrętnych",
      tooltip: "2 - Steered axles (number)",
      group: "2_steered",
      inputType: "number",
      value: "1",
      hidden: true,
    },
    {
      key: "{{steeredAxlesPosition}}",
      label: "Pozycja osi skrętnych",
      tooltip: "2 - Steered axles (position)",
      group: "2_steered",
      inputType: "text",
      value: "A1",
      hidden: true,
    },
    {
      key: "{{poweredAxlesNumber}}",
      label: "Liczba osi napędowych",
      tooltip: "3 - Powered axles (number)",
      group: "3_powered",
      inputType: "number",
      value: "1",
      hidden: true,
    },
    {
      key: "{{poweredAxlesPosition}}",
      label: "Pozycja osi napędowych",
      tooltip: "3 - Powered axles (position)",
      group: "3_powered",
      inputType: "text",
      value: "A2",
      hidden: true,
    },
    {
      key: "{{automationType}}",
      label: "Typ automatyzacji",
      tooltip: "3.1 - non-automated/automated/fully automated",
      group: "3_powered",
      inputType: "select",
      selectOptions: [
        { value: "non-automated", label: "non-automated" },
        { value: "automated", label: "automated" },
        { value: "fully automated", label: "fully automated" },
        { value: "...", label: "dots" },
      ],
      value: "...",
    },

    // === SEKCJA 4-12 - Main dimensions ===
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{wheelbase}}",
      label: "Rozstaw osi",
      tooltip: "4 - Wheelbase (np. 4325 [mm])",
      group: "4_dimensions",
      inputType: "number",
      disabled: true,
    },
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{axleSpacing}}",
      label: "Odstęp między osiami",
      tooltip: "4.1 - Axle spacing (np. 4325 [mm])",
      group: "4_dimensions",
      inputType: "number",
      disabled: true,
    },
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{length}}",
      label: "Długość",
      tooltip: "5 - Length (np. 8040 [mm])",
      group: "5_width",
      inputType: "number",
      disabled: true,
    },
    {
      key: "{{elongatedCabs}}",
      label: "Przedłużone kabiny",
      tooltip: "5.2 - Elongated Cabs complying with Article 9a: yes/no",
      group: "5_width",
      inputType: "select",
      selectOptions: [
        { value: "yes", label: "yes" },
        { value: "no", label: "no" },
      ],
      value: "no",
      hidden: true,
    },
    {
      key: "{{aerodynamicDevice}}",
      label: "Urządzenie aerodynamiczne",
      tooltip:
        "5.3 - Vehicle equipped with aerodynamic device front/rear/not equipped",
      group: "5_width",
      inputType: "select",
      selectOptions: [
        { value: "front", label: "front" },
        { value: "rear", label: "rear" },
        { value: "not equipped", label: "not equipped" },
      ],
      value: "not equipped",
      hidden: true,
    },
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{width}}",
      label: "Szerokość",
      tooltip: "6 - Width (np. 2265 [mm])",
      group: "5_width",
      inputType: "number",
      disabled: true,
    },
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{height}}",
      label: "Wysokość",
      tooltip: "7 - Height (np. 3120 [mm])",
      group: "7_height",
      inputType: "number",
      disabled: true,
    },
    {
      key: "{{frontToCouplingDistance}}",
      label: "Odległość do sprzęgła",
      tooltip: "9 - Distance between front end and centre of coupling device",
      group: "9_distance",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      // ustalany na podstawie obiektu wheelbases - sztywne zmienne
      key: "{{rearOverhang}}",
      label: "Zwis tylny",
      tooltip: "12 - Rear overhang (np. 2564 [mm])",
      group: "12_overhang",
      inputType: "number",
      disabled: true,
    },

    // === SEKCJA 13 - Mass in running order ===
    {
      key: "{{massInRunningOrder}}",
      label: "Masa w stanie gotowym",
      tooltip:
        "13 - Mass in running order / gotowy do jazdy - masa na pusto pojazdu tylko z kierowcą",
      group: "13_mass",
      inputType: "number",
    },
    {
      key: "{{massAxle1}}",
      label: "Masa na osi 1",
      tooltip: "13.1 - Distribution axle 1 (np. 1900 kg)",
      group: "13_mass",
      inputType: "number",
    },
    {
      key: "{{massAxle2}}",
      label: "Masa na osi 2",
      tooltip: "13.1 - Distribution axle 2 (np. 2820 kg)",
      group: "13_mass",
      inputType: "number",
    },
    {
      key: "{{actualMass}}",
      label: "Rzeczywista masa",
      tooltip: "13.2 - Actual mass of the vehicle (np. 4645 kg)",
      group: "13_mass",
      inputType: "number",
    },
    {
      key: "{{additionalMassAlternative}}",
      label: "Dodatkowa masa (napęd alternatywny)",
      tooltip: "13.3 - Additional mass for alternative propulsion",
      group: "13_mass",
      inputType: "text",
      value: "...",
      hidden: true,
    },

    // === SEKCJA 16 - Technically permissible maximum masses ===
    {
      key: "{{maxLadenMass}}",
      label: "Maks. dopuszczalna masa całkowita",
      tooltip:
        "16.1 - Technically permissible maximum laden mass (np. 6800 kg). Wartość uzupełniana automatycznie, na podstawie pola 'Masa pojazdu' ze zlecenia.",
      group: "16_max_masses",
      inputType: "number",
      formatter: "computed",
      renderAppendKg: true,
    },
    {
      key: "{{maxMassAxle1}}",
      label: "Maks. dopuszczalna masa na osi 1",
      tooltip: "16.2 - Technically permissible mass on axle 1 (np. 2300 kg)",
      group: "16_max_masses",
      inputType: "number",
    },
    {
      key: "{{maxMassAxle2}}",
      label: "Maks. dopuszczalna masa na osi 2",
      tooltip: "16.2 - Technically permissible mass on axle 2 (np. 5200 kg)",
      group: "16_max_masses",
      inputType: "number",
    },
    {
      key: "{{maxMassAxleGroup1}}",
      label: "Maks. masa na grupie osi 1",
      tooltip: "16.3 - Technically permissible mass on axle group 1",
      group: "16_max_masses",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{maxMassAxleGroup2}}",
      label: "Maks. masa na grupie osi 2",
      tooltip: "16.3 - Technically permissible mass on axle group 2",
      group: "16_max_masses",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{maxMassCombination}}",
      label: "Maks. dopuszczalna masa zestawu - dot. hak",
      tooltip:
        "16.4 - Technically permissible maximum mass of combination. Wartość należy wpisać z 'kg'. Dotyczy Hak",
      group: "16_max_masses",
      inputType: "text",
      formatter: "computed",
      renderAppendKg: true,
    },

    // === SEKCJA 17 - Intended registration masses ===
    {
      key: "{{intendedMaxLadenMass}}",
      label: "Przewidywana maks. masa całkowita",
      tooltip:
        "17.1 - Intended maximum permissible laden mass (np. 6800 kg). Wartość uzupełniana automatycznie, na podstawie pola 'Masa pojazdu' ze zlecenia.",
      group: "17_intended",
      inputType: "number",
      formatter: "computed",
    },
    {
      key: "{{intendedMaxMassAxle1}}",
      label: "Przewidywana maks. masa na osi 1",
      tooltip: "17.2 - Intended max mass on axle 1 (np. 2300 kg)",
      group: "17_intended",
      inputType: "number",
    },
    {
      key: "{{intendedMaxMassAxle2}}",
      label: "Przewidywana maks. masa na osi 2",
      tooltip: "17.2 - Intended max mass on axle 2 (np. 5200 kg)",
      group: "17_intended",
      inputType: "number",
    },
    {
      key: "{{intendedMaxMassAxleGroup1}}",
      label: "Przewidywana maks. masa na grupie osi 1",
      tooltip: "17.3 - Intended max mass on axle group 1",
      group: "17_intended",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{intendedMaxMassAxleGroup2}}",
      label: "Przewidywana maks. masa na grupie osi 2",
      tooltip: "17.3 - Intended max mass on axle group 2",
      group: "17_intended",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{intendedMaxMassCombination}}",
      label: "Przewidywana maks. masa zestawu  - dot. hak",
      tooltip:
        "17.4 - Intended max mass of combination. Wartość należy wpisać z 'kg'. Dotyczy Hak",
      group: "17_intended",
      inputType: "text",
      formatter: "computed",
      renderAppendKg: true,
    },

    // === SEKCJA 18 - Towable mass ===
    {
      key: "{{towableDrawbar}}",
      label: "Masa holowana (przyczepa z dyszlem)",
      tooltip: "18.1 - Drawbar trailer",
      group: "18_towable",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "N/A",
        "Mercedes Benz Tourist Line HighDeck ": "N/A",
        "Iveco Tourist Line": "N/A",
      },
      valueFallback: "N/A",
      hidden: true,
      renderAppendKg: true,
    },
    {
      // Wartość zależy od haka holowniczego w opcjach zamówienia (checked
      // lub required, patrz utils/tow-hook.ts) - "N/A", gdy hak nieaktywny,
      // w przeciwnym razie 2000 kg (Iveco, MB HighDeck) albo 1500 kg
      // (MB Tourist Line). Wcześniej była to sztywna wartość per zabudowa,
      // niezależna od tego, czy hak faktycznie jest w zamówieniu.
      key: "{{towableCentreAxle}}",
      label: "Masa holowana (przyczepa centralna)",
      tooltip: "18.3 - Centre-axle trailer",
      group: "18_towable",
      inputType: "text",
      formatter: "computed",
      hidden: true,
      renderAppendKg: true,
    },
    {
      // jw. - zależne od haka holowniczego (utils/tow-hook.ts): 750 kg, gdy
      // hak aktywny (niezależnie od zabudowy), inaczej "N/A".
      key: "{{towableUnbraked}}",
      label: "Masa holowana (przyczepa niehamowana)",
      tooltip: "18.4 - Unbraked trailer",
      group: "18_towable",
      inputType: "text",
      formatter: "computed",
      hidden: true,
      renderAppendKg: true,
    },

    // === SEKCJA 19 - Static mass ===
    {
      // jw. - zależne od haka holowniczego (utils/tow-hook.ts): 100 kg, gdy
      // hak aktywny (niezależnie od zabudowy), inaczej "N/A".
      key: "{{staticMassCoupling}}",
      label: "Maks. dopuszczalna masa statyczna na sprzęgle",
      tooltip:
        "19 - Technically permissible maximum static mass at coupling point",
      group: "19_static",
      inputType: "text",
      formatter: "computed",
      hidden: true,
      renderAppendKg: true,
    },

    // ── Power plant (20–26.2) ─────────────────────────────────────
    {
      key: "{{engineManufacturer}}",
      label: "Producent silnika",
      tooltip: "20 - Manufacturer of the engine",
      group: "20_engine",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "Daimler AG",
        "Mercedes Benz Tourist Line HighDeck ": "Mercedes-Benz AG",
        "Iveco Tourist Line": "FPT industrial SPA",
      },
    },
    {
      key: "{{engineCode}}",
      label: "Kod silnika oznaczony na silniku",
      tooltip: "21 - Engine code as marked on the engine",
      group: "20_engine",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "654",
        "Mercedes Benz Tourist Line HighDeck ": "654",
        "Iveco Tourist Line": "F1CFL4115*M",
      },
    },
    {
      key: "{{engineWorkingPrinciple}}",
      label: "Zasada działania",
      tooltip: "22 - Working principle",
      group: "20_engine",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "4 stroke / self ignition",
        "Mercedes Benz Tourist Line HighDeck ": "Compression ignition",
        "Iveco Tourist Line": "Compression ignition",
      },
      requiredTranslation: true,
    },
    {
      key: "{{isPureElectric}}",
      label: "Pojazd czysto elektryczny",
      tooltip: "23 - Pure electric",
      group: "20_engine",
      inputType: "select",
      selectOptions: [
        { value: "yes", label: "Tak" },
        { value: "no", label: "Nie" },
      ],
      value: "no",
      hidden: true,
    },
    {
      key: "{{isHybridElectric}}",
      label: "Pojazd hybrydowy (elektryczny)",
      tooltip: "23.1 - Hybrid [electric] vehicle: yes/no",
      group: "20_engine",
      inputType: "select",
      selectOptions: [
        { value: "yes", label: "Tak" },
        { value: "no", label: "Nie" },
        { value: "na", label: "N/A" },
      ],
      value: "no",
      hidden: true,
    },
    {
      key: "{{cylindersNumberAndArrangement}}",
      label: "Liczba i układ cylindrów",
      tooltip: "24 - Number and arrangement of cylinders",
      group: "20_engine",
      inputType: "text",
      value: "4 in line",
      hidden: true,
    },
    {
      key: "{{engineCapacity}}",
      label: "Pojemność silnika",
      tooltip: "25 - Engine capacity",
      group: "20_engine",
      inputType: "number",
      orderDerived: true,
      orderPath: "engineCapacity",
      formatter: "computed",
      disabled: true,
      // hidden: true,
    },
    {
      key: "{{fuelType}}",
      label: "Paliwo",
      tooltip:
        "26 - Fuel: Diesel/petrol/LPG/CNG-Biomethane/LNG/Ethanol/Biodiesel/Hydrogen",
      group: "20_engine",
      inputType: "text",
      value: "Diesel",
      hidden: true,
    },
    {
      key: "{{fuelFeedType}}",
      label: "Rodzaj zasilania paliwem",
      tooltip: "26.1 - Mono fuel/Bi fuel/Flex fuel/Dual-fuel",
      group: "20_engine",
      inputType: "text",
      value: "Mono fuel",
      hidden: true,
    },
    {
      key: "{{dualFuelType}}",
      label: "Typ instalacji dual-fuel (dotyczy tylko dual-fuel)",
      tooltip:
        "26.2 - (Dual-fuel only) Type 1A/Type 1B/Type 2A/Type 2B/Type 3B",
      group: "20_engine",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },

    // ── Maximum power (27.1–27.4) ────────────────────────────────
    {
      key: "{{maxNetPowerCombustionValue}}",
      label: "Maksymalna moc netto (silnik spalinowy) [kW]",
      tooltip:
        "27.1 – Maximum net power value (internal combustion engine), in kW",
      group: "27_power",
      inputType: "number",
      value: {
        "Mercedes Benz Tourist Line": "140",
        "Mercedes Benz Tourist Line HighDeck ": "140",
        "Iveco Tourist Line": "152",
      },
      hidden: true,
    },
    {
      key: "{{maxNetPowerCombustionSpeed}}",
      label:
        "Prędkość obrotowa przy maksymalnej mocy netto (silnik spalinowy) min⁻¹]",
      tooltip:
        "27.1 – Engine speed at which the maximum net power is declared, in min⁻¹ (rpm)",
      group: "27_power",
      inputType: "number",
      value: {
        "Mercedes Benz Tourist Line": "3800",
        "Mercedes Benz Tourist Line HighDeck ": "3800",
        "Iveco Tourist Line": "3500",
      },
      hidden: true,
    },
    {
      key: "{{maxHourlyOutput}}",
      label: "Maksymalna moc godzinowa",
      tooltip: "27.2 - Maximum hourly output",
      group: "27_power",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{maxNetPowerElectric}}",
      label: "Maksymalna moc netto (silnik elektryczny)",
      tooltip: "27.3 - Maximum net power (electric motor)",
      group: "27_power",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{max30MinPowerElectric}}",
      label: "Maksymalna moc 30-minutowa (silnik elektryczny)",
      tooltip: "27.4 - Maximum 30 minutes power (electric motor)",
      group: "27_power",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{gearboxType}}",
      label: "Skrzynia biegów (typ)",
      tooltip: "28 - Gearbox (type)",
      group: "27_power",
      inputType: "text",
      orderDerived: true,
      orderPath: "gearbox", // trzeba przetłumaczyć
      formatter: "computed",
      hidden: true,
      disabled: true,
    },

    // ── Maximum speed (29) ───────────────────────────────────────
    {
      key: "{{maxSpeed}}",
      label: "Prędkość maksymalna",
      tooltip: "29 - Maximum speed",
      group: "29_speed",
      inputType: "text",
      value: "100 km/h",
      hidden: true,
    },

    // ── Axles and suspension (30, 32, 33, 35) ───────────────────
    {
      key: "{{axle1Track}}",
      label: "Rozstaw kół — oś 1",
      tooltip: "30 - Axle(s) track: axle 1",
      group: "30_axles",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "1685 mm",
        "Mercedes Benz Tourist Line HighDeck ": "1706 mm",
        "Iveco Tourist Line": "1723 mm",
      },
      hidden: true,
    },
    {
      key: "{{axle2Track}}",
      label: "Rozstaw kół — oś 2",
      tooltip: "30 - Axle(s) track: axle 2",
      group: "30_axles",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "1723 mm",
        "Mercedes Benz Tourist Line HighDeck ": "1517 mm",
        "Iveco Tourist Line": "1661 mm",
      },
      hidden: true,
    },
    {
      key: "{{loadableAxlePosition}}",
      label: "Położenie osi obciążalnej(ych)",
      tooltip: "32 - Position of loadable axle(s)",
      group: "30_axles",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{driveAxleAirSuspension}}",
      label: "Oś(e) napędowa(e) z zawieszeniem pneumatycznym lub równoważnym",
      tooltip:
        "33 - Drive axle(s) fitted with air suspension or equivalent: yes/no",
      group: "30_axles",
      inputType: "select",
      selectOptions: [
        { value: "yes", label: "Tak" },
        { value: "no", label: "Nie" },
      ],
      value: "no",
      hidden: true,
    },
    {
      key: "{{axle1TyreWheelCombination}}",
      label: "Kombinacja opon/kół — oś 1",
      tooltip: "35 - Tyre/wheel combination: axle 1",
      group: "30_axles",
      inputType: "textarea",
      value: {
        "Mercedes Benz Tourist Line": "205/75R16C 110/108 R-5,5Jx16 ET111",
        "Mercedes Benz Tourist Line HighDeck ":
          "225/75 R 16C R 121/120 5,5Jx16 ET111",
        "Iveco Tourist Line": "225/75R16 6Jx 16 H2 ET124 121/120",
      },
    },
    {
      key: "{{axle2TyreWheelCombination}}",
      label: "Kombinacja opon/kół — oś 2",
      tooltip: "35 - Tyre/wheel combination: axle 2",
      group: "30_axles",
      inputType: "textarea",
      value: {
        "Mercedes Benz Tourist Line": "205/75R16C 110/108 R-5,5Jx16 ET111",
        "Mercedes Benz Tourist Line HighDeck ":
          "225/75 R 16C R 121/120 6Jx16 ET124",
        "Iveco Tourist Line": "225/75R16 6Jx 16 H2 ET124 121/120",
      },
    },

    // ── Brakes (36–37) ───────────────────────────────────────────
    {
      key: "{{trailerBrakeConnections}}",
      label: "Przyłącza hamulca przyczepy",
      tooltip:
        "36 - Trailer brake connections: mechanical/electric/pneumatic/hydraulic",
      group: "36_brakes",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{trailerBrakingFeedLinePressure}}",
      label: "Ciśnienie w przewodzie zasilającym układu hamulcowego przyczepy",
      tooltip: "37 - Pressure in feed line for trailer braking system",
      group: "36_brakes",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },

    // ── Bodywork (38, 39, 41, 42, 42.1–42.3, 43) ────────────────
    {
      key: "{{bodyworkCode}}",
      label: "Kod nadwozia",
      tooltip: "38 - Code for bodywork",
      group: "38_bodywork",
      inputType: "text",
      value: "CA",
      hidden: true,
    },
    {
      key: "{{vehicleClass}}",
      label: "Klasa pojazdu",
      tooltip:
        "39 - Class of vehicle: class I/Class II/Class III/Class A/Class B. Powyżej 22 pasażerów: Klasa I - miejski (city) miejsca stojące i siedzące / Klasa II - międzymiastowy - miejsca siedzące i stojące / klasa III - tylko siedzące / Do 22 pasażerów:  Class A - miejski do 22 pasażerów (siedzących i stojących razem) /  Class B - turystyczny",
      group: "38_bodywork",
      inputType: "select",
      selectOptions: [
        { value: "I", label: "I" },
        { value: "II", label: "II" },
        { value: "III", label: "III" },
        { value: "A", label: "A" },
        { value: "B", label: "B" },
      ],
      formatter: "computed",
      disabled: true,
    },
    {
      key: "{{doorsNumberAndConfiguration}}",
      label: "Liczba i konfiguracja drzwi",
      tooltip: "41 - Number and configuration of doors",
      group: "38_bodywork",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "2; 1,1",
        "Mercedes Benz Tourist Line HighDeck ": "3: 1 left, 2 right",
        "Iveco Tourist Line": "2; 2R",
      },
    },
    {
      key: "{{seatingPositionsCount}}",
      label: "Liczba miejsc siedzących",
      tooltip:
        "42 - Number of seating positions (including the driver) - ustalana na podstawie specyfikacji",
      group: "38_bodywork",
      inputType: "number",
      formatter: "computed",
      orderDerived: true,
      orderPath: "places.variants[0].sittingPlacesCount",
      hidden: true,
    },
    {
      key: "{{stationaryOnlySeats}}",
      label: "Miejsca siedzące przeznaczone do użytku tylko podczas postoju",
      tooltip:
        "42.1 - Seat(s) designated for use only when the vehicle is stationary",
      group: "38_bodywork",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{passengerSeatingPositions}}",
      label: "Liczba miejsc siedzących dla pasażerów (dolny/górny pokład)",
      tooltip:
        "42.2 - Number of passenger seating positions: (lower deck)/(upper deck) (including the driver)",
      group: "38_bodywork",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{wheelchairAccessiblePositions}}",
      label: "Liczba miejsc dostępnych dla użytkowników wózków inwalidzkich",
      tooltip: "42.3 - Number of wheelchair user accessible position",
      group: "38_bodywork",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{standingPlacesCount}}",
      label: "Liczba miejsc stojących",
      tooltip:
        "43 - Number of standing places - ustalana na podstawie specyfikacji",
      group: "38_bodywork",
      inputType: "number",
      formatter: "computed",
      orderDerived: true,
      orderPath: "places.variants[0].standingPlacesCount",
      hidden: true,
    },

    // ── Coupling device (44, 45.1) ──────────────────────────────
    {
      key: "{{couplingDeviceApprovalNumber}}",
      label:
        "Numer/znak homologacji urządzenia sprzęgającego (jeśli zamontowane)",
      tooltip:
        "44 - Approval number or approval mark of coupling device (if fitted)",
      group: "44_coupling",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "E13 55R-01 4217",
        "Mercedes Benz Tourist Line HighDeck ": "N/A",
        "Iveco Tourist Line": "N/A",
      },
      valueFallback: "N/A",
    },
    {
      key: "{{couplingCharacteristicValues}}",
      label: "Wartości charakterystyczne sprzęgu (D/V/S/U)",
      tooltip: "45.1 - Characteristics values: D / V / S / U",
      group: "44_coupling",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "D: 23,5 / V: - / S: 150 / U: -",
        "Mercedes Benz Tourist Line HighDeck ": "D: - / V: - / S: - / U: -",
        "Iveco Tourist Line": "D: - / V: - / S: - / U: -",
      },
    },

    // ── Environmental performances (46–49.7) ────────────────────
    {
      key: "{{soundLevelStationary}}",
      label: "Poziom hałasu — postój",
      tooltip: "46 - Sound level, Stationary",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "82",
        "Mercedes Benz Tourist Line HighDeck ": "82",
        "Iveco Tourist Line": "85",
      },
      hidden: true,
    },
    {
      key: "{{soundLevelStationaryEngineSpeed}}",
      label: "Poziom hałasu — postój, przy prędkości obrotowej silnika",
      tooltip: "46 - Sound level, Stationary: at engine speed",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "2850",
        "Mercedes Benz Tourist Line HighDeck ": "2850",
        "Iveco Tourist Line": "2625",
      },
      hidden: true,
    },
    {
      key: "{{soundLevelDriveBy}}",
      label: "Poziom hałasu — przejazd",
      tooltip: "46 - Sound level, Drive-by",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "71",
        "Mercedes Benz Tourist Line HighDeck ": "71",
        "Iveco Tourist Line": "75",
      },
      hidden: true,
    },
    {
      key: "{{exhaustEmissionLevel}}",
      label: "Poziom emisji spalin",
      tooltip: "47 - Exhaust emission level",
      group: "46_environmental",
      inputType: "text",
      value: "Euro VI E",
      hidden: true,
    },
    {
      key: "{{exhaustEmissionsRegulatoryAct}}",
      label:
        "Numer podstawowego aktu prawnego i najnowszego aktu zmieniającego",
      tooltip:
        "48 - Exhaust emissions: number of the base regulatory act and latest amending regulatory act applicable",
      group: "46_environmental",
      inputType: "text",
      value: "595/2009*2022/2383E",
      hidden: true,
    },
    {
      key: "{{whscCo}}",
      label: "WHSC — CO",
      tooltip: "48 - Exhaust emissions, WHSC test procedure (EURO VI): CO",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "17,46",
        "Mercedes Benz Tourist Line HighDeck ": "17,46",
        "Iveco Tourist Line": "16,711",
      },
      hidden: true,
    },
    {
      key: "{{whscThc}}",
      label: "WHSC — THC",
      tooltip: "48 - Exhaust emissions, WHSC test procedure (EURO VI): THC",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "3,77",
        "Mercedes Benz Tourist Line HighDeck ": "3,77",
        "Iveco Tourist Line": "5,931",
      },
      hidden: true,
    },
    {
      key: "{{whscNmhc}}",
      label: "WHSC — NMHC",
      tooltip: "48 - Exhaust emissions, WHSC test procedure (EURO VI): NMHC",
      group: "46_environmental",
      inputType: "text",
      value: "-",
      hidden: true,
    },
    {
      key: "{{whscNox}}",
      label: "WHSC — NOx",
      tooltip: "48 - Exhaust emissions, WHSC test procedure (EURO VI): NOx",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "5,41",
        "Mercedes Benz Tourist Line HighDeck ": "5,41",
        "Iveco Tourist Line": "115,196",
      },
      hidden: true,
    },
    {
      key: "{{whscThcNox}}",
      label: "WHSC — THC + NOx",
      tooltip:
        "48 - Exhaust emissions, WHSC test procedure (EURO VI): THC + NOx",
      group: "46_environmental",
      inputType: "text",
      value: "-",
      hidden: true,
    },
    {
      key: "{{whscNh3}}",
      label: "WHSC — NH3",
      tooltip: "48 - Exhaust emissions, WHSC test procedure (EURO VI): NH3",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "0,90",
        "Mercedes Benz Tourist Line HighDeck ": "0,90",
        "Iveco Tourist Line": "0,201",
      },
      hidden: true,
    },
    {
      key: "{{whscParticulatesMass}}",
      label: "WHSC — Cząstki stałe (masa)",
      tooltip:
        "48 - Exhaust emissions, WHSC test procedure (EURO VI): Particulates (mass)",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "1,02",
        "Mercedes Benz Tourist Line HighDeck ": "1,02",
        "Iveco Tourist Line": "0,836",
      },
      hidden: true,
    },
    {
      key: "{{whscParticlesNumber}}",
      label: "WHSC — Cząstki stałe (liczba)",
      tooltip:
        "48 - Exhaust emissions, WHSC test procedure (EURO VI): Particles (number)",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "0,03*10^11 / kWH",
        "Mercedes Benz Tourist Line HighDeck ": "0,03*10^11 / kWH",
        "Iveco Tourist Line": "0,098E+11",
      },
      hidden: true,
    },
    {
      key: "{{whtcCo}}",
      label: "WHTC — CO",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): CO",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "159,98",
        "Mercedes Benz Tourist Line HighDeck ": "159,98",
        "Iveco Tourist Line": "30,403",
      },
      hidden: true,
    },
    {
      key: "{{whtcNox}}",
      label: "WHTC — NOx",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): NOx",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "22,93",
        "Mercedes Benz Tourist Line HighDeck ": "22,93",
        "Iveco Tourist Line": "142,678",
      },
      hidden: true,
    },
    {
      key: "{{whtcNmhc}}",
      label: "WHTC — NMHC",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): NMHC",
      group: "46_environmental",
      inputType: "text",
      value: "-",
      hidden: true,
    },
    {
      key: "{{whtcThc}}",
      label: "WHTC — THC",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): THC",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "54,72",
        "Mercedes Benz Tourist Line HighDeck ": "54,72",
        "Iveco Tourist Line": "33,634",
      },
      hidden: true,
    },
    {
      key: "{{whtcCh4}}",
      label: "WHTC — CH4",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): CH4",
      group: "46_environmental",
      inputType: "text",
      value: "0",
      hidden: true,
    },
    {
      key: "{{whtcNh3}}",
      label: "WHTC — NH3",
      tooltip: "48 - Exhaust emissions, WHTC test procedure (EURO VI): NH3",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "0,09",
        "Mercedes Benz Tourist Line HighDeck ": "0,09",
        "Iveco Tourist Line": "0,039",
      },
      hidden: true,
    },
    {
      key: "{{whtcParticulatesMass}}",
      label: "WHTC — Cząstki stałe (masa)",
      tooltip:
        "48 - Exhaust emissions, WHTC test procedure (EURO VI): Particulates (mass)",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "1,89",
        "Mercedes Benz Tourist Line HighDeck ": "1,89",
        "Iveco Tourist Line": "2,6136",
      },
      hidden: true,
    },
    {
      key: "{{whtcParticlesNumber}}",
      label: "WHTC — Cząstki stałe (liczba)",
      tooltip:
        "48 - Exhaust emissions, WHTC test procedure (EURO VI): Particles (number)",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "0,07*10^11 / kWh",
        "Mercedes Benz Tourist Line HighDeck ": "0,07*10^11 / kWh",
        "Iveco Tourist Line": "0,886E+11",
      },
      hidden: true,
    },
    {
      key: "{{smokeCorrectedAbsorptionCoefficient}}",
      label: "Skorygowany współczynnik pochłaniania dymu",
      tooltip: "48.1 - Smoke corrected absorption coefficient",
      group: "46_environmental",
      inputType: "text",
      value: {
        "Mercedes Benz Tourist Line": "N/A",
        "Mercedes Benz Tourist Line HighDeck ": "N/A",
        "Iveco Tourist Line": "0,5025 m-1",
      },
      valueFallback: "N/A",
      hidden: true,
    },
    {
      key: "{{co2FuelConsumption}}",
      label: "Emisja CO2 / zużycie paliwa / zużycie energii elektrycznej",
      tooltip:
        "49 - CO2 emissions/fuel consumption/electric energy consumption",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{manufacturerRecordsFileHash}}",
      label: "Suma kontrolna (hash) pliku danych producenta",
      tooltip: "49.1 - Cryptographic hash of the manufacturer's records file",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{isZeroEmissionHeavyDutyVehicle}}",
      label: "Pojazd ciężarowy o zerowej emisji",
      tooltip: "49.2 - Zero emission heavy-duty vehicle: yes/no",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{isVocationalVehicle}}",
      label: "Pojazd specjalistyczny (zawodowy)",
      tooltip: "49.3 - Vocational vehicle: yes/no",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{customerInfoFileHash}}",
      label: "Suma kontrolna (hash) pliku informacji dla klienta",
      tooltip: "49.4 - Cryptographic hash of the customer information file",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{specificCo2Emissions}}",
      label: "Szczególna emisja CO2",
      tooltip: "49.5.1 - Specific CO2 emissions",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{averageOccupancy}}",
      label: "Średnia liczba osób",
      tooltip: "49.6 - Average occupancy (number of persons)",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{vehicleSubgroupGroup}}",
      label: "Podgrupa/grupa pojazdu",
      tooltip: "49.7 - Vehicle subgroup/group",
      group: "46_environmental",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },

    // ── Miscellaneous (51, 52, 54, 55, 56) ─────────────────────
    {
      key: "{{specialPurposeVehicleDesignation}}",
      label:
        "Oznaczenie dla pojazdów specjalnego przeznaczenia (zgodnie z pkt 5 część A załącznika I do Rozporządzenia (UE) 2018/858)",
      tooltip:
        "51 - For special purpose vehicles: designation in accordance with point 5 of Part A of Annex I to Regulation (EU) 2018/858",
      group: "51_misc",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
    {
      key: "{{remarks}}",
      label: "Uwagi",
      tooltip:
        "52 - Remarks, np. Telma Retarder is installed. / Vehicle equipped with Blind Spot Information System.",
      group: "51_misc",
      inputType: "textarea",
    },
    {
      key: "{{vehicleFittedWith}}",
      label: "Pojazd wyposażony w",
      tooltip:
        "54 - Vehicle fitted with: TPMS/ESS/AIF/AEBS/ISA/DDAW/ADDW/BSIS/EDR/DAM/ADS/Platooning",
      group: "51_misc",
      inputType: "multiselect",
      colSpan: 2,
      selectOptions: [
        { value: "TPMS", label: "TPMS" },
        { value: "ESS", label: "ESS" },
        { value: "AIF", label: "AIF" },
        { value: "AEBS", label: "AEBS" },
        { value: "ISA", label: "ISA" },
        { value: "DDAW", label: "DDAW" },
        { value: "ADDW", label: "ADDW" },
        { value: "BSIS", label: "BSIS" },
        { value: "EDR", label: "EDR" },
        { value: "DAM", label: "DAM" },
        { value: "ADS", label: "ADS" },
        { value: "Platooning", label: "Platooning" },
      ],
      value: {
        "Iveco Tourist Line": [
          "AIF",
          "ISA",
          "ESS",
          "AEBS",
          "TPMS",
          "DDAW",
          "BSIS",
          "ADDW",
        ],
        "Mercedes Benz Tourist Line": [
          "TPMS",
          "ESS",
          "AIF",
          "AEBS",
          "ISA",
          "DDAW",
          "ADDW",
          "BSIS",
          "EDR",
          "DAM",
          "ADS",
        ],
        "Mercedes Benz Tourist Line HighDeck ": [
          "TPMS",
          "ESS",
          "AIF",
          "AEBS",
          "ISA",
          "DDAW",
          "ADDW",
          "BSIS",
          "EDR",
          "DAM",
          "ADS",
          "Platooning",
        ],
      },
    },
    {
      key: "{{certifiedUnRegulation155}}",
      label: "Pojazd homologowany zgodnie z Regulaminem ONZ nr 155",
      tooltip: "55 - Vehicle certified in accordance with UN Regulation No 155",
      group: "51_misc",
      inputType: "text",
      value: "YES",
      hidden: true,
    },
    {
      key: "{{certifiedUnRegulation156}}",
      label: "Pojazd homologowany zgodnie z Regulaminem ONZ nr 156",
      tooltip: "56 - Vehicle certified in accordance with UN Regulation No 156",
      group: "51_misc",
      inputType: "text",
      value: "N/A",
      hidden: true,
    },
  ];

/**
 * Tworzy klucz do mapowania wartości w zależności od producenta i zabudowy.
 * Klucz ma format: "{producentName} {conversionName}"
 *
 * @param producentName - nazwa producenta (np. "Mercedes Benz")
 * @param conversionName - nazwa zabudowy (np. "Tourist Line HighDeck")
 * @returns połączony klucz (np. "Mercedes Benz Tourist Line HighDeck ")
 *
 * @example
 * const key = buildProducerConversionKey("Mercedes Benz", "Tourist Line HighDeck");
 * // key = "Mercedes Benz Tourist Line HighDeck "
 */
export function buildProducerConversionKey(
  producentName: string | undefined,
  conversionName: string | undefined,
): string {
  const parts = [producentName, conversionName].filter(
    (part): part is string => typeof part === "string" && part.length > 0,
  );
  return parts.join(" ");
}

/**
 * Mapowanie sekcji numerycznych na etykiety wyświetlane w formularzu
 */
export const CERTIFICATE_OF_CONFORMITY_GROUP_LABELS: Record<
  CertificateOfConformityVariable["group"],
  string
> = {
  "0_general": "Dane dokumentu",
  "0.1": "0.1 - Marka producenta",
  "0.2": "0.2 - Typ, wariant, wersja",
  "0.2.1": "0.2.1 - Nazwa handlowa",
  "0.2.2": "0.2.2 - Dane homologacji pojazdu bazowego",
  "0.2.2.1": "0.2.2.1 - Parametry homologacji wieloetapowej",
  "0.2.3": "0.2.3 - Identyfikatory rodzin",
  "0.4": "0.4 - Kategoria pojazdu",
  "0.5": "0.5 - Producent pojazdu",
  "0.5.1": "0.5.1 - Producent pojazdu bazowego",
  "0.6": "0.6 - Tablice homologacyjne i VIN",
  "0.9": "0.9 - Przedstawiciel producenta",
  "0.10": "0.10 - Numer VIN",
  "0.11": "0.11 - Data produkcji i zakończenie",
  footer: "Stopka dokumentu (miejsce)",
  // Sekcja 1 - General construction
  "1_general": "1 - Charakterystyka konstrukcyjna (liczba osi/kół)",
  "1.1": "1.1 - Oś z kołami bliźniaczymi",
  "2_steered": "2 - Osie skrętne",
  "3_powered": "3 - Osie napędowe i automatyzacja",
  // Main dimensions
  "4_dimensions": "4-5 - Rozstaw osi i długość",
  "5_width": "6 - Szerokość i wymiary dodatkowe",
  "7_height": "7 - Wysokość",
  "9_distance": "9 - Odległość do sprzęgła",
  "12_overhang": "12 - Zwis tylny",
  // Masses
  "13_mass": "13 - Masa w stanie gotowym",
  "16_max_masses": "16 - Maks. dopuszczalne masy",
  "17_intended": "17 - Przewidywane maks. masy rejestracyjne",
  "18_towable": "18 - Masa holowana",
  "19_static": "19 - Masa statyczna na sprzęgle",
  // Power plant
  "20_engine": "20-26 - Silnik (producent, kod, paliwo)",
  "27_power": "27-28 - Moc i skrzynia biegów",
  "29_speed": "29 - Prędkość maksymalna",
  // Axles
  "30_axles": "30-35 - Rozstaw kół, zawieszenie i opony",
  // Brakes
  "36_brakes": "36-37 - Hamulce i połączenia przyczepy",
  // Bodywork
  "38_bodywork": "38-43 - Nadwozie, klasa, drzwi, miejsca",
  // Coupling
  "44_coupling": "44-45 - Sprzęgło",
  // Environmental
  "46_environmental": "46-49 - Środowisko (hałas, emisje, CO2)",
  // Miscellaneous
  "51_misc": "51-56 - Uwagi i systemy",
};
